import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash, MagnifyingGlass, FileArrowUp, CheckCircle, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import IngredientFileImporter from "@/components/IngredientFileImporter";
import SanitizeIngredientsModal from "@/components/ingredients/SanitizeIngredientsModal";
import { EstimatedPriceBadge } from "@/components/IngredientCombobox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "Carnes, Aves e Peixes",
  "Laticínios e Ovos",
  "Hortifruti",
  "Secos e Grãos",
  "Confeitaria",
  "Panificação",
  "Temperos e Condimentos",
  "Óleos e Gorduras",
  "Bebidas e Líquidos",
  "Embalagens",
  "Outros",
];

export default function Ingredients() {
  const [ingredients, setIngredients] = useState([]);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sanitizeOpen, setSanitizeOpen] = useState(false);
  const { user } = useAuth();
  const isTeamMember = user?.is_employee || user?.has_team;
  const [activeTab, setActiveTab] = useState("company"); // "company" | "personal"
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [serverCategories, setServerCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    quantity_purchased: "",
    unit: "kg",
    purchase_price: "",
    peso_equivalente_g: "",
    category: ""
  });
  
  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchIngredients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Busca/filtro com debounce — a consulta roda direto no MongoDB (server-side)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) setPage(1);
      else fetchIngredients();
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterCategory]);

  const fetchIngredients = async () => {
    try {
      const { data } = await axios.get(`${API}/ingredients`, {
        params: {
          limit: PAGE_SIZE,
          skip: (page - 1) * PAGE_SIZE,
          search: searchQuery.trim() || undefined,
          category: filterCategory !== "all" ? filterCategory : undefined,
        },
      });
      setIngredients(data.items || []);
      setMeta({ total: data.total || 0, pages: data.pages || 1 });
      setServerCategories(data.categories || []);
    } catch (error) {
      toast.error("Erro ao carregar ingredientes");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        quantity_purchased: parseFloat(formData.quantity_purchased),
        purchase_price: parseFloat(formData.purchase_price),
        peso_equivalente_g: formData.peso_equivalente_g ? parseFloat(formData.peso_equivalente_g) : null,
        category: formData.category || null,
        scope: (isTeamMember && activeTab === "personal") ? "personal" : "company",
      };
      
      if (editingIngredient) {
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, data);
        toast.success("Ingrediente atualizado com sucesso!");
      } else {
        await axios.post(`${API}/ingredients`, data);
        toast.success("Ingrediente cadastrado com sucesso!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchIngredients();
    } catch (error) {
      toast.error("Erro ao salvar ingrediente");
    }
  };
  
  const handleEdit = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      name: ingredient.name,
      quantity_purchased: ingredient.quantity_purchased.toString(),
      unit: ingredient.unit,
      purchase_price: ingredient.purchase_price.toString(),
      peso_equivalente_g: ingredient.peso_equivalente_g ? ingredient.peso_equivalente_g.toString() : "",
      category: ingredient.category || ""
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja deletar este ingrediente?")) return;
    try {
      await axios.delete(`${API}/ingredients/${id}`);
      toast.success("Ingrediente deletado com sucesso!");
      fetchIngredients();
    } catch (error) {
      toast.error("Erro ao deletar ingrediente");
    }
  };

  const handleConfirmPrice = async (ingredient) => {
    try {
      await axios.put(`${API}/ingredients/${ingredient.id}`, { is_estimated: false, price_source: "manual" });
      toast.success(`Preço de "${ingredient.name}" confirmado!`);
      fetchIngredients();
    } catch (error) {
      toast.error("Erro ao confirmar preço");
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: "",
      quantity_purchased: "",
      unit: "kg",
      purchase_price: "",
      peso_equivalente_g: "",
      category: ""
    });
    setEditingIngredient(null);
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };
  
  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  // Busca e categoria já vêm filtradas do servidor; aqui só o escopo (empresa/pessoal)
  const filteredIngredients = ingredients.filter((ing) => {
    return !isTeamMember || (activeTab === "personal" ? ing.scope === "personal" : ing.scope !== "personal");
  });

  const usedCategories = serverCategories;
  
  if (loading) {
    return <div className="text-center py-12 text-zinc-500">Carregando...</div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-3xl font-bold tracking-tight text-zinc-950">Ingredientes</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">Gerencie seus ingredientes e insumos</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="sanitize-ingredients-btn"
            variant="outline"
            onClick={() => setSanitizeOpen(true)}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <span className="hidden sm:inline">✨ Sanitizar Insumos (SousChef AI)</span>
            <span className="sm:hidden">✨ Sanitizar</span>
          </Button>
          <Button
            data-testid="import-ingredients-btn"
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="border-zinc-300"
          >
            <FileArrowUp size={18} className="mr-2" />
            <span className="hidden sm:inline">Importar Arquivo</span>
            <span className="sm:hidden">Importar</span>
          </Button>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-ingredient-btn"
              className="bg-zinc-950 text-white hover:bg-zinc-800"
            >
              <Plus size={18} className="mr-2" />
              {t('ing_new')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                {editingIngredient ? t('ing_edit') : t('ing_new')}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {editingIngredient ? t('ing_edit') : t('ing_new')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome</Label>
                <Input
                  id="name"
                  data-testid="ingredient-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Açúcar"
                  required
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                  Categoria <span className="text-zinc-400 font-normal">(opcional)</span>
                </Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="ingredient-category-select" className="mt-1.5">
                    <SelectValue placeholder="Selecione uma categoria..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem categoria</SelectItem>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="quantity" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Quantidade</Label>
                  <Input
                    id="quantity"
                    data-testid="ingredient-quantity-input"
                    type="number"
                    step="0.001"
                    value={formData.quantity_purchased}
                    onChange={(e) => setFormData({ ...formData, quantity_purchased: e.target.value })}
                    placeholder="1"
                    required
                    className="mt-1.5"
                  />
                </div>
                
                <div>
                  <Label htmlFor="unit" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Unidade</Label>
                  <Select 
                    value={formData.unit} 
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger data-testid="ingredient-unit-select" className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="l">l</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="unidades">unidades</SelectItem>
                      <SelectItem value="maço">maço</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="price" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Preço de Compra (R$)</Label>
                <Input
                  id="price"
                  data-testid="ingredient-price-input"
                  type="number"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                  placeholder="6.00"
                  required
                  className="mt-1.5"
                />
              </div>
              
              {['unidades', 'maço', 'dente', 'xícara'].includes(formData.unit) && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label htmlFor="peso-equiv" className="text-xs font-semibold text-blue-800">
                    Peso equivalente por {formData.unit} (g/ml) <span className="font-normal text-blue-500">(opcional)</span>
                  </Label>
                  <Input
                    id="peso-equiv"
                    data-testid="ingredient-peso-equivalente-input"
                    type="number"
                    step="0.1"
                    value={formData.peso_equivalente_g}
                    onChange={(e) => setFormData({ ...formData, peso_equivalente_g: e.target.value })}
                    placeholder={`Ex: 1 ${formData.unit} = quantos gramas?`}
                    className="mt-1.5 bg-white"
                  />
                  <p className="text-[10px] text-blue-600 mt-1">
                    Ex: 1 maço de salsão = 300g, 1 ovo = 50g, 1 dente de alho = 5g
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => handleDialogChange(false)} data-testid="cancel-ingredient-btn">
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-ingredient-btn" className="bg-zinc-950 text-white hover:bg-zinc-800">
                  {editingIngredient ? "Atualizar" : "Salvar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <SanitizeIngredientsModal open={sanitizeOpen} onOpenChange={setSanitizeOpen} onMerged={fetchIngredients} />
      <IngredientFileImporter
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={fetchIngredients}
      />
      
      <Card className="border-zinc-200 shadow-sm rounded-sm">
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col gap-3">
            {isTeamMember && (
              <div className="flex border-b border-zinc-200 gap-0 -mb-1">
                <button
                  onClick={() => setActiveTab("company")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "company" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                  data-testid="tab-company-ingredients"
                >
                  {user?.business_name || t("empresa")}
                </button>
                <button
                  onClick={() => setActiveTab("personal")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "personal" ? "border-violet-600 text-violet-700" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                  data-testid="tab-personal-ingredients"
                >
                  Ingredientes Pessoais
                </button>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-base md:text-lg font-semibold tracking-tight text-zinc-900 shrink-0">
              {isTeamMember && activeTab === "personal" ? "Ingredientes Pessoais" : "Lista de Ingredientes"}
            </CardTitle>
            <div className="flex items-center gap-2 flex-1 max-w-lg">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  data-testid="ingredient-search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("ing_search")}
                  className="pl-9 h-9 text-sm"
                />
              </div>
              {usedCategories.length > 0 && (
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[140px] md:w-[180px] h-9 text-sm" data-testid="filter-category">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("all")}</SelectItem>
                    {usedCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0">
          {filteredIngredients.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              {searchQuery || filterCategory !== "all"
                ? "Nenhum ingrediente encontrado com esse filtro."
                : <>{t("ing_no_ingredients")}</>}
            </div>
          ) : (
            <>
              {/* Mobile view: cards */}
              <div className="sm:hidden space-y-2">
                {filteredIngredients.map((ingredient) => (
                  <div key={ingredient.id} className="border border-zinc-200 rounded-lg p-3" data-testid={`ingredient-row-m-${ingredient.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{ingredient.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {ingredient.is_estimated && (
                            <span className="inline-flex items-center gap-1">
                              <EstimatedPriceBadge testId={`estimated-badge-m-${ingredient.id}`} />
                              <button onClick={() => handleConfirmPrice(ingredient)} title="Confirmar preço" data-testid={`confirm-price-m-${ingredient.id}`} className="text-emerald-600 hover:text-emerald-800">
                                <CheckCircle size={14} weight="fill" />
                              </button>
                            </span>
                          )}
                          {ingredient.category && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 rounded text-zinc-600">{ingredient.category}</span>}
                          {ingredient.peso_equivalente_g && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">~{ingredient.peso_equivalente_g}g</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEdit(ingredient)} data-testid={`edit-ingredient-${ingredient.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(ingredient.id)} data-testid={`delete-ingredient-${ingredient.id}`}>
                          <Trash size={14} className="text-rose-600" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-zinc-500"><span className="font-mono font-bold text-zinc-800">{ingredient.quantity_purchased}</span> {ingredient.unit}</span>
                      <span className="text-zinc-500">Compra: <span className="font-mono font-bold text-zinc-800">{formatCurrency(ingredient.purchase_price)}</span></span>
                      <span className="text-zinc-500 ml-auto">Custo: <span className="font-mono font-bold text-emerald-700">{formatCurrency(ingredient.cost_per_unit)}/{ingredient.unit === 'kg' || ingredient.unit === 'l' ? ingredient.unit.slice(0, 1) : ingredient.unit}</span></span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop view: table */}
              <div className="hidden sm:block overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nome</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Categoria</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Qtd. Comprada</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Preço Compra</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo/Unidade</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIngredients.map((ingredient) => (
                  <TableRow key={ingredient.id} className="table-row" data-testid={`ingredient-row-${ingredient.id}`}>
                    <TableCell className="font-medium text-zinc-900">
                      {ingredient.name}
                      {ingredient.is_estimated && (
                        <span className="ml-2 inline-flex items-center gap-1 align-middle">
                          <EstimatedPriceBadge testId={`estimated-badge-${ingredient.id}`} />
                          <button onClick={() => handleConfirmPrice(ingredient)} title="Confirmar este preço como correto" data-testid={`confirm-price-${ingredient.id}`} className="text-emerald-600 hover:text-emerald-800">
                            <CheckCircle size={16} weight="fill" />
                          </button>
                        </span>
                      )}
                      {ingredient.peso_equivalente_g && (
                        <span className="ml-2 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                          ~{ingredient.peso_equivalente_g}g/{ingredient.unit}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-600 text-xs">
                      {ingredient.category ? (
                        <span className="px-2 py-0.5 bg-zinc-100 rounded text-zinc-700">{ingredient.category}</span>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-700">
                      <span className="font-mono">{ingredient.quantity_purchased}</span> {ingredient.unit}
                    </TableCell>
                    <TableCell className="text-right font-mono number text-zinc-900">
                      {formatCurrency(ingredient.purchase_price)}
                    </TableCell>
                    <TableCell className="text-right font-mono number text-zinc-900">
                      {formatCurrency(ingredient.cost_per_unit)}/{ingredient.unit === 'kg' || ingredient.unit === 'l' ? ingredient.unit.slice(0, 1) : ingredient.unit}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center space-x-2">
                        <Button data-testid={`edit-ingredient-${ingredient.id}`} variant="ghost" size="sm" onClick={() => handleEdit(ingredient)}>
                          <Pencil size={16} />
                        </Button>
                        <Button data-testid={`delete-ingredient-${ingredient.id}`} variant="ghost" size="sm" onClick={() => handleDelete(ingredient.id)}>
                          <Trash size={16} className="text-rose-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
              </div>
              {meta.pages > 1 && (
                <div className="flex items-center justify-between mt-4" data-testid="ingredients-pagination">
                  <p className="text-xs text-zinc-500" data-testid="ingredients-page-info">
                    {t("pg_showing")} {filteredIngredients.length} {t("of")} {meta.total} • {t("pg_page")} {page}/{meta.pages}
                  </p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="ingredients-prev-page">
                      <CaretLeft size={14} className="mr-1" /> {t("previous")}
                    </Button>
                    <Button variant="outline" size="sm" className="h-8" disabled={page >= meta.pages} onClick={() => setPage(page + 1)} data-testid="ingredients-next-page">
                      {t("next")} <CaretRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
